"use client";

import { ChevronLeft, ChevronRight } from "lucide-react";
import { useRef, useState } from "react";
import {
  buildCloudinarySrcSet,
  withCloudinaryTransform,
} from "../../elements/image/cloudinary";
import { IconVideoPlay, IconX } from "../../icons";
import { cn } from "../../lib/utils";
import type { GalleryImageData } from "../image-gallery/image-gallery.types";
import { OverlayGallery } from "./overlay/overlay-gallery";
import type { ProductMediaGalleryProps } from "./product-media-gallery.types";
import {
  buildMediaGalleryImages,
  resolveGalleryMicrocopies,
} from "./product-media-gallery.utils";

const SWIPE_THRESHOLD = 50;
const GALLERY_FALLBACK_WIDTH = 1024;

type MediaGalleryVideoSource = {
  mode: "embed" | "file";
  src: string;
};

const VIDEO_FILE_EXTENSIONS = [
  ".m3u8",
  ".mov",
  ".mp4",
  ".ogg",
  ".ogv",
  ".webm",
] as const;

function resolveMediaGalleryVideoSource(
  videoUrl: string | undefined
): MediaGalleryVideoSource | undefined {
  const trimmedVideoUrl = videoUrl?.trim();

  if (!trimmedVideoUrl) {
    return;
  }

  const absoluteVideoUrl = trimmedVideoUrl.startsWith("//")
    ? `https:${trimmedVideoUrl}`
    : trimmedVideoUrl;

  try {
    const parsedUrl = new URL(absoluteVideoUrl);

    if (!(parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:")) {
      return;
    }

    const isDirectVideoFile = VIDEO_FILE_EXTENSIONS.some((extension) =>
      parsedUrl.pathname.toLowerCase().endsWith(extension)
    );

    return {
      mode: isDirectVideoFile ? "file" : "embed",
      src: parsedUrl.toString(),
    };
  } catch {
    // new URL() throws TypeError for malformed URLs — treat them as "no video"
    return;
  }
}

export function ProductMediaGallery({
  productName,
  mainAsset,
  assetViews,
  beautyshots,
  videos,
  videoUrl,
  microcopies: microcopiesProp,
  aspectRatio = "1:1",
}: Readonly<ProductMediaGalleryProps>) {
  const images: GalleryImageData[] = buildMediaGalleryImages(
    productName,
    mainAsset,
    assetViews,
    beautyshots,
    videos
  );
  const microcopies = resolveGalleryMicrocopies(microcopiesProp);

  const [activeIndex, setActiveIndex] = useState(0);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const touchStartX = useRef<number | null>(null);

  if (images.length === 0) {
    return null;
  }

  const hasPrevious = activeIndex > 0;
  const hasNext = activeIndex < images.length - 1;
  const currentImage = images[activeIndex];
  const firstVideoIndex = images.findIndex(
    (image) => image.mediaType === "video"
  );
  const hasInlineVideoItems = firstVideoIndex !== -1;
  const isCurrentSlideVideo = currentImage.mediaType === "video";
  const inlineVideoSource = isCurrentSlideVideo
    ? resolveMediaGalleryVideoSource(currentImage.videoUrl)
    : undefined;
  const legacyVideoSource = resolveMediaGalleryVideoSource(videoUrl);
  const hasLegacyVideo = Boolean(legacyVideoSource) && !hasInlineVideoItems;

  function handlePrevious() {
    if (hasPrevious) {
      setActiveIndex((i) => i - 1);
    }
  }

  function handleNext() {
    if (hasNext) {
      setActiveIndex((i) => i + 1);
    }
  }

  function handleTouchStart(e: React.TouchEvent) {
    touchStartX.current = e.touches[0].clientX;
  }

  function handleTouchEnd(e: React.TouchEvent) {
    if (touchStartX.current === null) {
      return;
    }

    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > SWIPE_THRESHOLD) {
      if (diff > 0) {
        handleNext();
      } else {
        handlePrevious();
      }
    }

    touchStartX.current = null;
  }

  const formattedCounter = microcopies.imageCounter
    .replace("{current}", String(activeIndex + 1))
    .replace("{total}", String(images.length));

  return (
    <>
      <div
        className={cn(
          aspectRatio === "4:3" ? "aspect-[4/3]" : "aspect-square",
          "grid w-full select-none grid-rows-[1fr_auto] overflow-hidden md:aspect-auto md:h-[var(--pdp-gallery-height,max(0px,calc(100vh_-_var(--site-header-height,0px)_-_var(--pdp-gallery-top-offset,_0px))))]"
        )}
        onTouchEnd={handleTouchEnd}
        onTouchStart={handleTouchStart}
      >
        {/* Image area (cannot overlap controls row) */}
        {inlineVideoSource ? (
          <div className="relative min-h-0 overflow-hidden bg-black">
            <iframe
              allow="autoplay; encrypted-media; picture-in-picture"
              className="h-full w-full"
              loading="eager"
              src={inlineVideoSource.src}
              title={currentImage.alt}
            />
          </div>
        ) : (
          <button
            aria-label={microcopies.viewGallery}
            className="relative isolate block min-h-0 w-full cursor-zoom-in overflow-hidden bg-whitesmoke"
            onClick={() => setIsOverlayOpen(true)}
            type="button"
          >
            <img
              alt={currentImage.alt}
              className="h-full w-full object-contain mix-blend-multiply"
              draggable={false}
              // Only the initial slide is the LCP candidate; later slides are user-driven.
              fetchPriority={activeIndex === 0 ? "high" : undefined}
              loading="eager"
              sizes="(min-width: 1024px) calc(100vw - 437px), 100vw"
              src={withCloudinaryTransform(
                currentImage.src,
                GALLERY_FALLBACK_WIDTH
              )}
              srcSet={buildCloudinarySrcSet(currentImage.src)}
            />
          </button>
        )}

        {/* Controls row (dedicated space, no overlap with image) */}
        <div className="flex items-center justify-between p-4">
          <button
            aria-label={microcopies.viewGallery}
            className="flex h-12 w-12 items-center justify-center bg-white hover:bg-gray-50"
            onClick={() => setIsOverlayOpen(true)}
            type="button"
          >
            <svg
              aria-hidden="true"
              fill="none"
              height="14"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.4"
              viewBox="0 0 14 14"
              width="14"
            >
              <line x1="1" x2="13" y1="13" y2="1" />
              <polyline points="10,1 13,1 13,4" />
              <polyline points="4,13 1,13 1,10" />
            </svg>
          </button>

          <div className="flex items-center gap-2">
            {images.length > 1 && (
              <div className="flex items-center bg-white">
                <button
                  aria-label={microcopies.previous}
                  className="flex h-12 w-12 cursor-pointer items-center justify-center hover:bg-gray-50 disabled:opacity-30"
                  disabled={!hasPrevious}
                  onClick={handlePrevious}
                  type="button"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>

                <span
                  aria-atomic="true"
                  aria-live="polite"
                  className="min-w-[3.5rem] select-none px-1 text-center font-brand font-semibold text-[11px] uppercase leading-[20px] tracking-[0.04em]"
                >
                  {formattedCounter}
                </span>

                <button
                  aria-label={microcopies.next}
                  className="flex h-12 w-12 cursor-pointer items-center justify-center hover:bg-gray-50 disabled:opacity-30"
                  disabled={!hasNext}
                  onClick={handleNext}
                  type="button"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}

            {hasInlineVideoItems && (
              <button
                aria-label={microcopies.playVideo}
                className="flex h-12 w-12 items-center justify-center bg-white hover:bg-gray-50"
                onClick={() => setActiveIndex(firstVideoIndex)}
                type="button"
              >
                <IconVideoPlay
                  className="h-[18px] w-[18px]"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1"
                />
              </button>
            )}

            {hasLegacyVideo && legacyVideoSource && (
              <button
                aria-label={microcopies.playVideo}
                className="flex h-12 w-12 items-center justify-center bg-white hover:bg-gray-50"
                onClick={() => setIsVideoOpen(true)}
                type="button"
              >
                <IconVideoPlay
                  className="h-[18px] w-[18px]"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1"
                />
              </button>
            )}
          </div>
        </div>
      </div>

      <OverlayGallery
        activeIndex={activeIndex}
        images={images}
        isVisible={isOverlayOpen}
        microcopies={{
          close: microcopies.close,
          previous: microcopies.previous,
          next: microcopies.next,
          imageCounter: microcopies.imageCounter,
        }}
        onClose={() => setIsOverlayOpen(false)}
        onImageChange={setActiveIndex}
      />

      {isVideoOpen && legacyVideoSource && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <button
            aria-label={microcopies.close}
            className="absolute inset-0 bg-black/80"
            onClick={() => setIsVideoOpen(false)}
            type="button"
          />
          <div className="relative z-10 w-full max-w-4xl px-4">
            {legacyVideoSource.mode === "file" ? (
              /* biome-ignore lint/a11y/useMediaCaption: product video, no captions available */
              <video // NOSONAR
                className="w-full rounded"
                controls
                playsInline
                preload="metadata"
                src={legacyVideoSource.src}
              />
            ) : (
              <iframe
                allow="autoplay; encrypted-media; picture-in-picture"
                className="aspect-video w-full rounded"
                loading="eager"
                src={legacyVideoSource.src}
                title={`${productName} video`}
              />
            )}

            <button
              aria-label={microcopies.close}
              className="-top-10 absolute right-4 text-white hover:text-gray-300"
              onClick={() => setIsVideoOpen(false)}
              type="button"
            >
              <IconX className="!h-5 !w-5" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
